import React, { useState, useEffect, useCallback, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/24/outline';
import { format, parseISO, subWeeks, subMonths } from 'date-fns';
import { useManagement } from '../context/ManagementContext';
import { useAuth } from '../context/AuthContext';
import managementService from '../api/managementService';
import { Search, Filter, X, ChevronDown, Check } from 'lucide-react';
import AdvancedEntitySelector from './AdvancedEntitySelector';
import type { Gateway, Customer } from '../context/ManagementContext';

interface BackfillJob {
  id: string;
  status: 'pending' | 'analyzing' | 'previewing' | 'executing' | 'completed' | 'failed' | 'cancelled';
  readingType: 'energy' | 'water';
  deviceIds: string[];
  targetStartDate: string;
  targetEndDate: string;
  strategy: 'arbitrary' | 'previous-period';
  sourceStartDate?: string;
  sourceEndDate?: string;
  periodType?: 'week' | 'month';
  totalReadings: number;
  processedReadings: number;
  progress: number;
  analysisResult?: {
    totalGapHours: number;
    gapPeriods: Array<{ start: string; end: string; hours: number }>;
    devicesWithGaps: Array<{ deviceId: string; gapHours: number }>;
  };
  previewResult?: {
    sourceDataPoints: number;
    targetDataPoints: number;
    estimatedReadings: number;
    sampleData: Array<{
      deviceId: string;
      timestamp: string;
      value: number;
      isBackfilled: boolean;
    }>;
  };
  error?: string;
  createdAt: string;
  completedAt?: string;
}

export const BackfillTool: React.FC = () => {
  const { t } = useTranslation();
  const { dispatch } = useManagement();
  const { state: authState } = useAuth();
  
  const [selectedDevices, setSelectedDevices] = useState<string[]>([]);
  const [readingType, setReadingType] = useState<'energy' | 'water'>('energy');
  const [targetStartDate, setTargetStartDate] = useState<string>('');
  const [targetEndDate, setTargetEndDate] = useState<string>('');
  const [strategy, setStrategy] = useState<'arbitrary' | 'previous-period'>('previous-period');
  const [sourceStartDate, setSourceStartDate] = useState<string>('');
  const [sourceEndDate, setSourceEndDate] = useState<string>('');
  const [periodType, setPeriodType] = useState<'week' | 'month'>('week');
  
  const [analysisResult, setAnalysisResult] = useState<BackfillJob['analysisResult']>(undefined);
  const [previewResult, setPreviewResult] = useState<BackfillJob['previewResult']>(undefined);
  const [currentJob, setCurrentJob] = useState<BackfillJob | null>(null);
  const [recentJobs, setRecentJobs] = useState<BackfillJob[]>([]);
  
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<'select' | 'analyze' | 'preview' | 'execute' | 'monitor'>('select');
  
  // Device search and filter state
  const [deviceSearch, setDeviceSearch] = useState('');
  const [selectedGateway, setSelectedGateway] = useState<Gateway | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [searchedDevices, setSearchedDevices] = useState<any[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showGatewaySelector, setShowGatewaySelector] = useState(false);
  const [showCustomerSelector, setShowCustomerSelector] = useState(false);
  const [selectedDeviceDetails, setSelectedDeviceDetails] = useState<Map<string, any>>(new Map());

  const searchDevices = async () => {
    setSearchLoading(true);
    try {
      const params: any = {
        limit: 100,
        deviceType: readingType,
      };
      
      if (deviceSearch.trim()) {
        params.search = deviceSearch.trim();
      }
      
      if (selectedGateway) {
        params.gatewayId = selectedGateway.id;
      }
      
      if (selectedCustomer) {
        params.customerId = selectedCustomer.id;
      }
      
      const response = await managementService.device.getAll(params);
      setSearchedDevices(response.data || []);
    } catch (error) {
      console.error('Error searching devices:', error);
      setSearchedDevices([]);
    } finally {
      setSearchLoading(false);
    }
  };


  // Initial search on mount and when filters change
  useEffect(() => {
    searchDevices();
  }, [readingType, selectedGateway?.id, selectedCustomer?.id]);

  // Debounced search for text input
  useEffect(() => {
    const timer = setTimeout(() => {
      searchDevices();
    }, 300);
    
    return () => clearTimeout(timer);
  }, [deviceSearch]);

  const fetchDevices = async () => {
    try {
      const response = await managementService.device.getAll();
      dispatch({ type: 'SET_DEVICES', payload: response.data || [] });
    } catch (error) {
      console.error('Error fetching devices:', error);
    }
  };

  useEffect(() => {
    fetchDevices();
    fetchRecentJobs();
  }, []);

  useEffect(() => {
    if (currentJob && currentJob.status === 'executing') {
      const interval = setInterval(() => {
        fetchJobStatus(currentJob.id);
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [currentJob]);

  const apiCall = useCallback(async (endpoint: string, method: string = 'GET', body?: any) => {
    const baseUrl = import.meta.env.VITE_API_URL ? new URL('/api', import.meta.env.VITE_API_URL).toString() : 'http://localhost:3000/api';
    const response = await fetch(`${baseUrl}/v1/backfill${endpoint}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authState.token}`,
      },
      body: body ? JSON.stringify(body) : undefined,
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'API request failed');
    }
    
    return response.json();
  }, [authState.token]);

  const fetchRecentJobs = async () => {
    try {
      const jobs = await apiCall('/jobs?limit=10');
      setRecentJobs(jobs);
    } catch (error) {
      console.error('Error fetching recent jobs:', error);
    }
  };

  const fetchJobStatus = async (jobId: string) => {
    try {
      const job = await apiCall(`/jobs/${jobId}`);
      setCurrentJob(job);
      
      if (job.status === 'completed' || job.status === 'failed') {
        fetchRecentJobs();
      }
    } catch (error) {
      console.error('Error fetching job status:', error);
    }
  };

  const analyzeGaps = async () => {
    if (selectedDevices.length === 0 || !targetStartDate || !targetEndDate) {
      alert(t('Please select devices and date range'));
      return;
    }
    
    setLoading(true);
    try {
      const result = await apiCall('/analyze', 'POST', {
        deviceIds: selectedDevices,
        readingType,
        startDate: targetStartDate,
        endDate: targetEndDate,
      });
      
      setAnalysisResult(result);
      setStep('analyze');
    } catch (error) {
      console.error('Error analyzing gaps:', error);
      alert(t('backfill.errorAnalyzingGaps') + ' ' + (error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const previewBackfill = async () => {
    setLoading(true);
    try {
      const body: any = {
        deviceIds: selectedDevices,
        readingType,
        targetRange: {
          start: targetStartDate,
          end: targetEndDate,
        },
        strategy,
      };
      
      if (strategy === 'arbitrary') {
        body.sourceRange = {
          start: sourceStartDate,
          end: sourceEndDate,
        };
      } else {
        body.periodType = periodType;
      }
      
      const result = await apiCall('/preview', 'POST', body);
      setPreviewResult(result);
      setStep('preview');
    } catch (error) {
      console.error('Error previewing backfill:', error);
      alert(t('backfill.errorPreviewingBackfill') + ' ' + (error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const executeBackfill = async () => {
    setLoading(true);
    try {
      const body: any = {
        deviceIds: selectedDevices,
        readingType,
        targetRange: {
          start: targetStartDate,
          end: targetEndDate,
        },
        strategy,
      };
      
      if (strategy === 'arbitrary') {
        body.sourceRange = {
          start: sourceStartDate,
          end: sourceEndDate,
        };
      } else {
        body.periodType = periodType;
      }
      
      const result = await apiCall('/execute', 'POST', body);
      setCurrentJob({ 
        id: result.jobId, // Backend returns 'jobId', frontend expects 'id'
        status: 'executing', 
        progress: 0,
        readingType,
        deviceIds: selectedDevices,
        targetStartDate,
        targetEndDate,
        strategy,
        totalReadings: 0,
        processedReadings: 0,
        createdAt: new Date().toISOString(),
      } as BackfillJob);
      setStep('monitor');
    } catch (error) {
      console.error('Error executing backfill:', error);
      alert(t('backfill.errorExecutingBackfill') + ' ' + (error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const calculateSourceDates = () => {
    if (!targetStartDate || !targetEndDate) return;
    
    const start = parseISO(targetStartDate);
    const end = parseISO(targetEndDate);
    
    if (strategy === 'previous-period') {
      const offset = periodType === 'week' ? subWeeks : subMonths;
      setSourceStartDate(format(offset(start, 1), 'yyyy-MM-dd'));
      setSourceEndDate(format(offset(end, 1), 'yyyy-MM-dd'));
    }
  };

  useEffect(() => {
    calculateSourceDates();
  }, [targetStartDate, targetEndDate, strategy, periodType]);


  return (
    <div className="space-y-6">
      {step === 'select' && (
        <div className="space-y-6">
          {/* Header with Reading Type */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Backfill Tool</h1>
              <p className="mt-1 text-sm text-gray-500">
                Fill data gaps by copying from another time period
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">Type:</label>
                <Listbox value={readingType} onChange={(value) => setReadingType(value as 'energy' | 'water')}>
                  <div className="relative">
                    <Listbox.Button className="relative cursor-default rounded-md border border-gray-300 bg-white py-1.5 pl-3 pr-8 text-left text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500">
                      <span className="block truncate">
                        {readingType === 'energy' ? 'Energy' : 'Water'}
                      </span>
                      <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                        <ChevronUpDownIcon className="h-3 w-3 text-gray-400" aria-hidden="true" />
                      </span>
                    </Listbox.Button>
                    <Transition
                      as={Fragment}
                      leave="transition ease-in duration-100"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                    >
                      <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                        <Listbox.Option
                          className={({ active }) =>
                            `relative cursor-default select-none py-2 pl-3 pr-9 ${
                              active ? 'bg-blue-600 text-white' : 'text-gray-900'
                            }`
                          }
                          value="energy"
                        >
                          {({ selected, active }) => (
                            <>
                              <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                Energy
                              </span>
                              {selected ? (
                                <span
                                  className={`absolute inset-y-0 right-0 flex items-center pr-4 ${
                                    active ? 'text-white' : 'text-blue-600'
                                  }`}
                                >
                                  <CheckIcon className="h-4 w-4" aria-hidden="true" />
                                </span>
                              ) : null}
                            </>
                          )}
                        </Listbox.Option>
                        <Listbox.Option
                          className={({ active }) =>
                            `relative cursor-default select-none py-2 pl-3 pr-9 ${
                              active ? 'bg-blue-600 text-white' : 'text-gray-900'
                            }`
                          }
                          value="water"
                        >
                          {({ selected, active }) => (
                            <>
                              <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                Water
                              </span>
                              {selected ? (
                                <span
                                  className={`absolute inset-y-0 right-0 flex items-center pr-4 ${
                                    active ? 'text-white' : 'text-blue-600'
                                  }`}
                                >
                                  <CheckIcon className="h-4 w-4" aria-hidden="true" />
                                </span>
                              ) : null}
                            </>
                          )}
                        </Listbox.Option>
                      </Listbox.Options>
                    </Transition>
                  </div>
                </Listbox>
              </div>
            </div>
          </div>

          {/* Search and Filters Bar */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex-1 max-w-md">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search devices by name, description, or slave ID..."
                    value={deviceSearch}
                    onChange={(e) => setDeviceSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  {deviceSearch && (
                    <button
                      onClick={() => setDeviceSearch('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-500">
                  {selectedDevices.length} selected
                </span>
                <button
                  type="button"
                  onClick={() => setShowFilters(!showFilters)}
                  className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md border ${
                    showFilters || selectedGateway || selectedCustomer
                      ? 'bg-blue-50 text-blue-700 border-blue-200'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <Filter className="w-4 h-4" />
                  Filters
                  <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
                </button>
              </div>
            </div>

            {/* Filter Controls */}
            {showFilters && (
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg border">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Gateway</label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowGatewaySelector(true)}
                      className="relative w-full cursor-default rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-left text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <span className="block truncate">
                        {selectedGateway ? (selectedGateway.name || selectedGateway.id) : 'All Gateways'}
                      </span>
                    </button>
                    {selectedGateway && (
                      <button
                        type="button"
                        onClick={() => setSelectedGateway(null)}
                        className="absolute inset-y-0 right-0 flex items-center pr-2"
                      >
                        <span className="text-gray-400 hover:text-gray-600">×</span>
                      </button>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Customer</label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowCustomerSelector(true)}
                      className="relative w-full cursor-default rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-left text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <span className="block truncate">
                        {selectedCustomer ? selectedCustomer.name : 'All Customers'}
                      </span>
                    </button>
                    {selectedCustomer && (
                      <button
                        type="button"
                        onClick={() => setSelectedCustomer(null)}
                        className="absolute inset-y-0 right-0 flex items-center pr-2"
                      >
                        <span className="text-gray-400 hover:text-gray-600">×</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Active Filters */}
            {(selectedGateway || selectedCustomer) && (
              <div className="flex flex-wrap gap-2">
                {selectedGateway && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                    Gateway: {selectedGateway.name || selectedGateway.id}
                  </span>
                )}
                {selectedCustomer && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                    Customer: {selectedCustomer.name}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Device Table */}
          <div className="bg-white shadow rounded-lg border border-gray-200">
            <div className="px-4 py-3 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-medium text-gray-900">Select Devices</h3>
                <div className="flex items-center gap-3">
                  {searchedDevices.length > 0 && (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          const deviceIds = searchedDevices.map((d: any) => d.id);
                          setSelectedDevices(deviceIds);
                          const newDetails = new Map(selectedDeviceDetails);
                          searchedDevices.forEach(device => {
                            newDetails.set(device.id, device);
                          });
                          setSelectedDeviceDetails(newDetails);
                        }}
                        className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                      >
                        Select All
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedDevices([]);
                          setSelectedDeviceDetails(new Map());
                        }}
                        className="text-sm text-red-600 hover:text-red-800 font-medium"
                      >
                        Clear All
                      </button>
                    </>
                  )}
                  <span className="text-sm text-gray-500">
                    {searchedDevices.length} devices found
                  </span>
                </div>
              </div>
            </div>

            <div className="max-h-96 overflow-y-auto">
              {searchLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                  <span className="ml-3 text-gray-500">Searching devices...</span>
                </div>
              ) : searchedDevices.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <div className="text-base mb-2">No devices found</div>
                  <div className="text-sm">
                    {deviceSearch || selectedGateway || selectedCustomer
                      ? "Try adjusting your search criteria"
                      : `No ${readingType} devices available`}
                  </div>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {searchedDevices.map((device: any) => (
                    <label key={device.id} className="flex items-center px-4 py-3 hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedDevices.includes(device.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedDevices([...selectedDevices, device.id]);
                            setSelectedDeviceDetails(prev => new Map(prev.set(device.id, device)));
                          } else {
                            setSelectedDevices(selectedDevices.filter(id => id !== device.id));
                            setSelectedDeviceDetails(prev => {
                              const newMap = new Map(prev);
                              newMap.delete(device.id);
                              return newMap;
                            });
                          }
                        }}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <div className="ml-4 flex-1 min-w-0">
                        <div className="flex items-center gap-3">
                          <div className="font-medium text-gray-900 truncate">
                            {device.name || `Device ${device.slaveId}`}
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                              {device.deviceType}
                            </span>
                            <span className="text-sm text-gray-500">
                              Slave: {device.slaveId}
                            </span>
                          </div>
                        </div>
                        <div className="mt-1 flex items-center gap-4 text-sm text-gray-500">
                          <span>Gateway: {device.gateway?.name || device.gatewayId}</span>
                          {device.customer && (
                            <span>Customer: {device.customer.name}</span>
                          )}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Selected Devices Summary */}
          {selectedDevices.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Check className="w-5 h-5 text-blue-600" />
                <span className="font-medium text-blue-900">
                  {selectedDevices.length} devices selected for backfill
                </span>
              </div>
              <div className="text-sm text-blue-700">
                Configure the target date range below and click "Analyze Gaps" to continue.
              </div>
            </div>
          )}
            
          {/* Date Range */}
          <div className="bg-white shadow rounded-lg border border-gray-200 p-6">
            <h3 className="text-base font-medium text-gray-900 mb-4">Target Date Range</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Target Start Date</label>
                <input
                  type="date"
                  value={targetStartDate}
                  onChange={(e) => setTargetStartDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Target End Date</label>
                <input
                  type="date"
                  value={targetEndDate}
                  onChange={(e) => setTargetEndDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            
            <div className="mt-6">
              <button
                onClick={analyzeGaps}
                disabled={loading || selectedDevices.length === 0 || !targetStartDate || !targetEndDate}
                className="w-full bg-blue-600 text-white py-2.5 px-4 rounded-md text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {loading ? 'Analyzing...' : 'Analyze Gaps'}
              </button>
            </div>
          </div>
        </div>
      )}
        
        {step === 'analyze' && analysisResult && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Gap Analysis Results</h1>
                <p className="mt-1 text-sm text-gray-500">
                  Review detected gaps and configure backfill strategy
                </p>
              </div>
            </div>
            
            <div className="bg-white shadow rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-medium text-gray-900">Gap Summary</h3>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center">
                  <div className="text-2xl font-bold text-yellow-800">{analysisResult.totalGapHours}</div>
                  <div className="ml-3">
                    <div className="text-sm font-medium text-yellow-800">Total gap hours</div>
                    <div className="text-xs text-yellow-600">Across {analysisResult.devicesWithGaps.length} devices</div>
                  </div>
                </div>
              </div>
            
              {analysisResult.gapPeriods.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Gap Periods Detected</h4>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {analysisResult.gapPeriods.map((gap: any, idx: number) => (
                      <div key={idx} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded text-sm">
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">
                            {format(parseISO(gap.start), 'MMM dd, HH:mm')} → {format(parseISO(gap.end), 'MMM dd, HH:mm')}
                          </div>
                        </div>
                        <span className="text-gray-600 font-medium">
                          {gap.hours}h
                        </span>
                      </div>
                    ))}
                  </div>
                  
                  {analysisResult.devicesWithGaps.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-gray-900 mb-2">Affected Devices</h4>
                      <div className="flex flex-wrap gap-2">
                        {analysisResult.devicesWithGaps.map((device: any, idx: number) => (
                          <span key={idx} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            {selectedDeviceDetails.get(device.deviceId)?.name || device.deviceId} ({device.gapHours}h)
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div className="bg-white shadow rounded-lg border border-gray-200 p-6">
              <h3 className="text-base font-medium text-gray-900 mb-4">Backfill Strategy</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Strategy</label>
              <Listbox value={strategy} onChange={(value) => setStrategy(value as 'arbitrary' | 'previous-period')}>
                <div className="relative">
                  <Listbox.Button className="relative w-full cursor-default rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-left text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500">
                    <span className="block truncate">
                      {strategy === 'previous-period' ? t('Previous Period (Auto)') : t('Arbitrary Range')}
                    </span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                      <ChevronUpDownIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
                    </span>
                  </Listbox.Button>
                  <Transition
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                      <Listbox.Option
                        className={({ active }) =>
                          `relative cursor-default select-none py-2 pl-3 pr-9 ${
                            active ? 'bg-blue-600 text-white' : 'text-gray-900'
                          }`
                        }
                        value="previous-period"
                      >
                        {({ selected, active }) => (
                          <>
                            <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                              {t('Previous Period (Auto)')}
                            </span>
                            {selected ? (
                              <span
                                className={`absolute inset-y-0 right-0 flex items-center pr-4 ${
                                  active ? 'text-white' : 'text-blue-600'
                                }`}
                              >
                                <CheckIcon className="h-4 w-4" aria-hidden="true" />
                              </span>
                            ) : null}
                          </>
                        )}
                      </Listbox.Option>
                      <Listbox.Option
                        className={({ active }) =>
                          `relative cursor-default select-none py-2 pl-3 pr-9 ${
                            active ? 'bg-blue-600 text-white' : 'text-gray-900'
                          }`
                        }
                        value="arbitrary"
                      >
                        {({ selected, active }) => (
                          <>
                            <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                              {t('Arbitrary Range')}
                            </span>
                            {selected ? (
                              <span
                                className={`absolute inset-y-0 right-0 flex items-center pr-4 ${
                                  active ? 'text-white' : 'text-blue-600'
                                }`}
                              >
                                <CheckIcon className="h-4 w-4" aria-hidden="true" />
                              </span>
                            ) : null}
                          </>
                        )}
                      </Listbox.Option>
                    </Listbox.Options>
                  </Transition>
                </div>
              </Listbox>
            </div>
            
                {strategy === 'previous-period' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Period Type</label>
                <Listbox value={periodType} onChange={(value) => setPeriodType(value as 'week' | 'month')}>
                  <div className="relative">
                    <Listbox.Button className="relative w-full cursor-default rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-left text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500">
                      <span className="block truncate">
                        {periodType === 'week' ? t('Previous Week') : t('Previous Month')}
                      </span>
                      <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                        <ChevronUpDownIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
                      </span>
                    </Listbox.Button>
                    <Transition
                      as={Fragment}
                      leave="transition ease-in duration-100"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                    >
                      <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                        <Listbox.Option
                          className={({ active }) =>
                            `relative cursor-default select-none py-2 pl-3 pr-9 ${
                              active ? 'bg-blue-600 text-white' : 'text-gray-900'
                            }`
                          }
                          value="week"
                        >
                          {({ selected, active }) => (
                            <>
                              <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                {t('Previous Week')}
                              </span>
                              {selected ? (
                                <span
                                  className={`absolute inset-y-0 right-0 flex items-center pr-4 ${
                                    active ? 'text-white' : 'text-blue-600'
                                  }`}
                                >
                                  <CheckIcon className="h-4 w-4" aria-hidden="true" />
                                </span>
                              ) : null}
                            </>
                          )}
                        </Listbox.Option>
                        <Listbox.Option
                          className={({ active }) =>
                            `relative cursor-default select-none py-2 pl-3 pr-9 ${
                              active ? 'bg-blue-600 text-white' : 'text-gray-900'
                            }`
                          }
                          value="month"
                        >
                          {({ selected, active }) => (
                            <>
                              <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                {t('Previous Month')}
                              </span>
                              {selected ? (
                                <span
                                  className={`absolute inset-y-0 right-0 flex items-center pr-4 ${
                                    active ? 'text-white' : 'text-blue-600'
                                  }`}
                                >
                                  <CheckIcon className="h-4 w-4" aria-hidden="true" />
                                </span>
                              ) : null}
                            </>
                          )}
                        </Listbox.Option>
                      </Listbox.Options>
                    </Transition>
                  </div>
                </Listbox>
                  </div>
                )}
                
                {strategy === 'arbitrary' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Source Start Date</label>
                      <input
                        type="date"
                        value={sourceStartDate}
                        onChange={(e) => setSourceStartDate(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Source End Date</label>
                      <input
                        type="date"
                        value={sourceEndDate}
                        onChange={(e) => setSourceEndDate(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                )}
              </div>
              
              <div className="flex justify-between pt-4 border-t border-gray-200">
                <button
                  onClick={() => setStep('select')}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Back
                </button>
                <button
                  onClick={previewBackfill}
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {loading ? 'Previewing...' : 'Preview Backfill'}
                </button>
              </div>
            </div>
          </div>
        )}
        
        {step === 'preview' && previewResult && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Backfill Preview</h1>
                <p className="mt-1 text-sm text-gray-500">
                  Review the data that will be backfilled
                </p>
              </div>
            </div>
            
            <div className="bg-white shadow rounded-lg border border-gray-200 p-6">
              <h3 className="text-base font-medium text-gray-900 mb-4">Data Summary</h3>
              <div className="grid grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">{previewResult.sourceDataPoints}</div>
                  <div className="text-sm text-gray-500 mt-1">Source Data Points</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-yellow-600">{previewResult.targetDataPoints}</div>
                  <div className="text-sm text-gray-500 mt-1">Existing Target Points</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">{previewResult.estimatedReadings}</div>
                  <div className="text-sm text-gray-500 mt-1">New Readings to Copy</div>
                </div>
              </div>
              
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-600">
                  <strong>Note:</strong> Data includes raw readings (typically every 5 minutes). 
                  For {Math.round(previewResult.estimatedReadings / selectedDevices.length / 12)} hours of data, 
                  this equals ~{Math.round(previewResult.estimatedReadings / selectedDevices.length)} readings per device.
                </p>
              </div>
            </div>
            
            {previewResult.sampleData.length > 0 && (
              <div className="bg-white shadow rounded-lg border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-base font-medium text-gray-900">Sample Data Preview</h3>
                </div>
                <div className="overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Device</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timestamp</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {previewResult.sampleData.map((sample: any, idx: number) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {selectedDeviceDetails.get(sample.deviceId)?.name || sample.deviceId}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {format(parseISO(sample.timestamp), 'MMM dd, HH:mm')}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                              {typeof sample.value === 'number' ? sample.value.toFixed(2) : String(sample.value)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
            
            <div className="flex justify-between">
              <button
                onClick={() => setStep('analyze')}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Back
              </button>
              <button
                onClick={executeBackfill}
                disabled={loading}
                className="px-6 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {loading ? 'Executing...' : 'Execute Backfill'}
              </button>
            </div>
          </div>
        )}
        
        {step === 'monitor' && currentJob && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Backfill Progress</h1>
                <p className="mt-1 text-sm text-gray-500">
                  Monitor the backfill job execution
                </p>
              </div>
            </div>
            
            <div className="bg-white shadow rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-medium text-gray-900">Job Status</h3>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  currentJob.status === 'completed' ? 'bg-green-100 text-green-800' :
                  currentJob.status === 'failed' ? 'bg-red-100 text-red-800' :
                  'bg-blue-100 text-blue-800'
                }`}>
                  {currentJob.status}
                </span>
              </div>
              {currentJob.error && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700">Error: {currentJob.error}</p>
                </div>
              )}
            
              {currentJob.status === 'executing' && (
                <div className="mt-4">
                  <div className="flex justify-between text-sm text-gray-600 mb-2">
                    <span>Progress</span>
                    <span>{typeof currentJob.progress === 'number' ? currentJob.progress.toFixed(1) : currentJob.progress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${currentJob.progress}%` }}
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    Processed {currentJob.processedReadings} of {currentJob.totalReadings} readings
                  </p>
                </div>
              )}
            </div>
            
            {currentJob.status === 'completed' && (
              <div className="bg-white shadow rounded-lg border border-gray-200 p-6">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <Check className="w-5 h-5 text-green-600 mr-2" />
                    <div>
                      <p className="text-green-800 font-medium">
                        Backfill completed successfully!
                      </p>
                      <p className="text-green-700 text-sm mt-1">
                        Total readings processed: {currentJob.totalReadings}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div className="flex justify-center">
              <button
                onClick={() => {
                  setStep('select');
                  setCurrentJob(null);
                  setAnalysisResult(undefined);
                  setPreviewResult(undefined);
                }}
                className="px-6 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Start New Backfill
              </button>
            </div>
          </div>
        )}
      
      {recentJobs.length > 0 && (
        <div className="bg-white shadow rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-base font-medium text-gray-900">Recent Backfill Jobs</h3>
          </div>
          <div className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Devices</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Progress</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {recentJobs.map((job: any) => (
                    <tr key={job.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {format(parseISO(job.createdAt), 'MMM dd, HH:mm')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 capitalize">
                          {job.readingType}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {job.deviceIds.length} devices
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          job.status === 'completed' ? 'bg-green-100 text-green-800' :
                          job.status === 'failed' ? 'bg-red-100 text-red-800' :
                          job.status === 'executing' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {job.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                        {typeof job.progress === 'number' ? job.progress.toFixed(1) : job.progress}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Gateway Selector Modal */}
      <AdvancedEntitySelector
        isOpen={showGatewaySelector}
        onClose={() => setShowGatewaySelector(false)}
        onSelect={(entity) => {
          setSelectedGateway(entity as Gateway);
          setShowGatewaySelector(false);
        }}
        entityType="gateway"
        title="Select Gateway"
      />

      {/* Customer Selector Modal */}
      <AdvancedEntitySelector
        isOpen={showCustomerSelector}
        onClose={() => setShowCustomerSelector(false)}
        onSelect={(entity) => {
          setSelectedCustomer(entity as Customer);
          setShowCustomerSelector(false);
        }}
        entityType="customer"
        title="Select Customer"
      />
    </div>
  );
};